import React from 'react';
import _ from 'lodash';
import moment from 'moment';

/** Reference for the month short names  */
const yearLabelsReference = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'Mai',
  'Jun',
  'Jul',
  'Aug',
  'Set',
  'Out',
  'Nov',
  'Dez',
];

/**
 * Get messages aggregate by id with the same month
 * @param {{_id: string, count: number}[]} totalMessages
 */
const getGraphicElementsIdAggregate = (totalMessages, period) => {
  const messagesIdAggregate = [];
  _.forEach(totalMessages, (message) => {
    const periodValue = getCurrentPeriodValue(message._id, period);
    const hasPeriod = _.find(
      messagesIdAggregate,
      (element) => getCurrentPeriodValue(element._id, period) === periodValue
    );
    if (!hasPeriod) {
      messagesIdAggregate.push({
        _id: moment(message._id).format('YYYY-MM').toString(),
        count: 0,
      });
    }
  });
  return messagesIdAggregate;
};

/**
 * Get the sum of messages aggregates by the months
 * @param {{_id: string, count: number}[]} totalGraphicElements
 */
const sumGraphicElements = (totalGraphicElements, period) => {
  const graphicElementIdAggregate = getGraphicElementsIdAggregate(
    totalGraphicElements,
    period
  );

  _.forEach(graphicElementIdAggregate, (graphicElement) => {
    const periodValue = getCurrentPeriodValue(graphicElement._id, period);
    const samePeriod = _.filter(
      totalGraphicElements,
      (element) => getCurrentPeriodValue(element._id, period) === periodValue
    );

    if (samePeriod.length > 0) {
      graphicElement.count = samePeriod
        .map((message) => message.count)
        .reduce((acc, current) => acc + current);
    }
  });

  return graphicElementIdAggregate;
};

/**
 * Get current period value for make the correct difference on time
 * @param {string} id
 * @param {string} period
 */
const getCurrentPeriodValue = (id, period) => {
  switch (period) {
    case 'month':
      return moment(id).month() + 1;
    default:
      return moment(id)[period]();
  }
};

/**
 * Generic get period chart category
 * @param {string[]} data
 * @param {(elements, element) => void} periodFunc
 */
const getPeriodChartCategory = (data, periodFunc) => {
  const elements = [];
  _.forEach(data, (element) => periodFunc(elements, element));
  return _.uniq(elements);
};

/**
 * Get hours chart categories
 * @param {string[]} data
 */
const getHoursChartCategory = (data) => {
  return getPeriodChartCategory(data, (elements, element) => {
    const date = moment(element._id);
    elements.push(
      `${String(date.hour()).padStart(2, '0')}h - ${String(
        date.date()
      ).padStart(2, '0')}/${String(date.month() + 1).padStart(2, '0')}`
    );
  });
};

/**
 * Get days chart categories
 * @param {string[]} data
 */
const getDaysChartCategory = (data) => {
  return getPeriodChartCategory(data, (elements, element) => {
    const date = moment(element._id);
    elements.push(
      `${String(date.date()).padStart(2, '0')}/${String(
        date.month() + 1
      ).padStart(2, '0')}`
    );
  });
};

/**
 * Get months chart categories
 * @param {string[]} data
 */
const getMonthsChartCategory = (data) => {
  return getPeriodChartCategory(data, (elements, element) => {
    const date = moment(element._id);
    elements.push(`${yearLabelsReference[date.month()]}`);
  });
};

/**
 * Get year chart categories
 * @param {string[]} data
 */
const getYearChartCategory = (data) => {
  return getPeriodChartCategory(data, (elements, element) => {
    const date = moment(element._id);
    elements.push(`${date.year()}`);
  });
};

/**
 * Generate random color string value for the dynamic number of bots
 * @returns: string
 */
const generateRandomColorString = () =>
  Math.floor(Math.random() * 16777215).toString(16);

/**
 * Update the chart XCategories array
 * @param {string} period
 * @param {{startDate: moment, endDate: moment}} data
 */
export const updateChartXCategories = (period, data) => {
  switch (period) {
    case 'hour':
      return getHoursChartCategory(data);
    case 'day':
      return getDaysChartCategory(data);
    case 'month':
      return getMonthsChartCategory(data);
    default:
      return getYearChartCategory(data);
  }
};

/**
 * Get filter period type for the graphic
 * @param {moment.Duration} difference
 */
export const getFilterPeriodType = (difference) => {
  if (difference.asDays() <= 1) {
    return 'hour';
  }

  if (difference.asDays() <= 31) {
    return 'day';
  }

  if (difference.asYears() < 1) {
    return 'month';
  }

  return 'year';
};

/**
 * Get the total messages from a a type of sender, fixing the values for the current filter selected
 * @param {{_id: string, count: number}[]} graphicElements
 * @param {string} period
 */
export const getTotalGraphic = (graphicElements, period) => {
  switch (period) {
    case 'month':
    case 'year':
      return sumGraphicElements(graphicElements, period);
    default:
      return graphicElements;
  }
};

/**
 * Sum the reports from a given array
 * @param {[]} messages
 */
export const sumReportsArray = (reports) => {
  if (!reports || reports.length < 1 || !Array.isArray(reports)) {
    return 0;
  }
  return reports
    .map((report) => report.count)
    .reduce((acc, current) => acc + current);
};

/**
 * Generate a random color that is not in the colors array yet
 * @param {string[]} colors
 */
export const getRandomColor = (colors) => {
  let color = `#${generateRandomColorString()}`;
  while (_.find(colors, color)) {
    color = `#${generateRandomColorString()}`;
  }
  colors.push(color);
  return color;
};
